import React, { useCallback } from 'react';
import { Field } from 'formik';
import PropTypes from 'prop-types';
import isEmpty from 'lodash.isempty';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import RoundedRadio from '../InputFields/RoundedRadio';
import TypeList from '../TypeList/TypeList';
import SinglePaymentMethod from './SinglePaymentMethod';
import { PAYMENT_METHOD_TYPES } from '../../util/enum/api/paymentTypes';
import themeColors from '../../assets/styles/themeColors';
import { mediaBelow, pxToRem, pxToRemMd } from '../../assets/styles/helper';
import { uFlexCenter } from '../../assets/styles/utility';
import { variables } from '../../assets/styles/variables';
import { ReactComponent as Caret } from '../../assets/images/svg/down.svg';
import { ReactComponent as Plus } from '../../assets/images/svg/plus.svg';
import Button from '../Button/Button';
import { BalanceCardIcon } from '../Portfolio/Balance/BalanceCard';
import AddPaymentMethod from './AddPaymentMethod';
import { removeFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import { ADD_PAYMENT_METHOD } from '../../constants/sessionStorage';

export const AddPaymentMethodButton = styled.div`
  border: 1px solid ${themeColors.colorBorderPrimary};
  background-color: ${themeColors.colorBackgroundPrimary};
  box-shadow: ${variables.shadow.boxShadow};
  margin-top: ${pxToRem(16)};
  ${({ disabled }) =>
    disabled &&
    `opacity: 0.5;
      pointer-events: none;
      `}
`;

export const AddPaymentMethodIconAndTextWrapper = styled.div`
  display: flex;
  flex: 1;
`;

export const AddPaymentMethodElement = styled(Button)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 0;
  box-shadow: none;
  padding: ${pxToRem(12.5)} ${pxToRem(16.5)};
  text-transform: none;
  justify-content: flex-start;
  font-weight: 600;
  color: ${themeColors.colorTextPrimary};
  font-size: ${pxToRem(16)};
  width: 100%;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: ${pxToRemMd(16)};
  }
`;

export const AddPaymentMethodIcon = styled.div`
  ${uFlexCenter};
  width: ${pxToRem(20)};
  height: ${pxToRem(20)};
  border-radius: 4px;
  background-color: ${themeColors.colorFocus};
  box-shadow: ${variables.shadow.boxShadow};
  margin-right: ${pxToRem(10)};
  flex-shrink: 0;
  padding: ${pxToRem(3)};
  color: ${themeColors.colorWhite};

  svg {
    width: ${pxToRem(20)};
    height: ${pxToRem(20)};
    color: ${themeColors.colorWhite};
  }
`;

export const HorizontalLine = styled.hr`
  width: 100%;
  height: 1px;
  background-color: ${themeColors.colorInputBorder};
  border-width: 0;
`;

export const AddPaymentMethodNote = styled.p`
  padding: 0px ${pxToRem(12.5)} ${pxToRem(12.5)} ${pxToRem(14)};
`;

export const AddPaymentMethodButtonText = styled.span``;

const ChoosePaymentMethod = ({
  paymentMethods,
  values,
  errorMessage,
  setErrorMessage,
  setSelectedPaymentMethod,
  clientExecutionPrice,
  isAccountBalanceDisabled,
  isInsufficientBalanceMessageHidden,
  totalAmountWithCreditCard,
  totalAmountWithoutCreditCard,
  isAddPaymentMethodModalOpen,
  setIsAddPaymentMethodModalOpen,
  setIsNewCardAdded,
  setIsNewACHAdded,
}) => {
  const { t } = useTranslation();

  const handleChangeValue = (paymentMethodUid) =>
    setSelectedPaymentMethod(
      paymentMethods.find(
        (method) =>
          method.PaymentMethodUid === paymentMethodUid &&
          !isPaymentMethodDisabled(method),
      ),
    );

  const isPaymentMethodDisabled = useCallback(
    (method) =>
      (method?.PaymentMethodType === PAYMENT_METHOD_TYPES.ACH &&
        method?.BankAccountSetupType === 2) ||
      (method?.PaymentMethodType === PAYMENT_METHOD_TYPES.CREDIT_CARD &&
        method?.CreditCardIsExpired) ||
      (method?.PaymentMethodType === PAYMENT_METHOD_TYPES.ACH &&
        totalAmountWithoutCreditCard > method?.MaximumLimit) ||
      (method?.PaymentMethodType === PAYMENT_METHOD_TYPES.CREDIT_CARD &&
        totalAmountWithCreditCard > method?.MaximumLimit) ||
      (method?.PaymentMethodType === PAYMENT_METHOD_TYPES.ACCOUNT_BALANCE &&
        isAccountBalanceDisabled),
    [
      isAccountBalanceDisabled,
      totalAmountWithCreditCard,
      totalAmountWithoutCreditCard,
    ],
  );

  return (
    <>
      {!isEmpty(paymentMethods) && (
        <>
          <TypeList gridRowGap={9}>
            {paymentMethods.map((method) => (
              <Field
                key={method.PaymentMethodUid}
                id={method.PaymentMethodUid}
                type="radio"
                name="paymentMethodUid"
                component={RoundedRadio}
                value={method.PaymentMethodUid}
                selected={
                  values.paymentMethodUid === method.PaymentMethodUid &&
                  !isPaymentMethodDisabled(method)
                }
                style={{ padding: '6px 16px' }}
                // disabled: BankAccountSetupType = 2; enabled: BankAccountSetupType = 3;
                disabled={isPaymentMethodDisabled(method)}
                // removeCheckbox={reachMethodLimit(method)}
                onChange={(e) => {
                  handleChangeValue(e.target.value);
                  if (errorMessage) {
                    setErrorMessage(!errorMessage);
                  }
                }}
              >
                <SinglePaymentMethod
                  method={method}
                  disabled={
                    method?.PaymentMethodType === PAYMENT_METHOD_TYPES.ACH &&
                    method?.BankAccountSetupType === 2
                  }
                  clientExecutionPrice={clientExecutionPrice}
                  isAccountBalanceDisabled={isAccountBalanceDisabled}
                  isInsufficientBalanceMessageHidden={
                    isInsufficientBalanceMessageHidden
                  }
                  totalAmountWithCreditCard={totalAmountWithCreditCard}
                  totalAmountWithoutCreditCard={totalAmountWithoutCreditCard}
                />
              </Field>
            ))}
          </TypeList>
          <AddPaymentMethodButton
            onClick={() => setIsAddPaymentMethodModalOpen(true)}
          >
            <AddPaymentMethodElement>
              <AddPaymentMethodIconAndTextWrapper>
                <AddPaymentMethodIcon>
                  <Plus />
                </AddPaymentMethodIcon>
                <AddPaymentMethodButtonText>
                  {t('buyWizard.postPaid.addPaymentMethod')}
                </AddPaymentMethodButtonText>
              </AddPaymentMethodIconAndTextWrapper>
              <BalanceCardIcon>
                <Caret />
              </BalanceCardIcon>
            </AddPaymentMethodElement>
            <AddPaymentMethodNote>
              {t('buyWizard.postPaid.chooseAchAndCreditCardOptions')}
            </AddPaymentMethodNote>
          </AddPaymentMethodButton>
        </>
      )}
      <AddPaymentMethod
        avoidClearingStorage
        paymentMethods={paymentMethods}
        isOpen={isAddPaymentMethodModalOpen}
        onClose={() => {
          removeFromSessionStorage(ADD_PAYMENT_METHOD);
          setIsAddPaymentMethodModalOpen(false);
        }}
        setIsNewCardAdded={setIsNewCardAdded}
        setIsNewACHAdded={setIsNewACHAdded}
        setIsAddPaymentMethodModalOpen={setIsAddPaymentMethodModalOpen}
      />
    </>
  );
};

ChoosePaymentMethod.propTypes = {
  paymentMethods: PropTypes.arrayOf({}),
  values: PropTypes.shape({
    paymentMethodUid: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({}),
    ]),
  }),
  setErrorMessage: PropTypes.func,
  errorMessage: PropTypes.bool,
  setSelectedPaymentMethod: PropTypes.func,
  clientExecutionPrice: PropTypes.number,
  isAddPaymentMethodModalOpen: PropTypes.bool,
  setIsAddPaymentMethodModalOpen: PropTypes.func,
  isAccountBalanceDisabled: PropTypes.bool,
  isInsufficientBalanceMessageHidden: PropTypes.bool,
  totalAmountWithCreditCard: PropTypes.number,
  totalAmountWithoutCreditCard: PropTypes.number,
  setIsNewCardAdded: PropTypes.func,
  setIsNewACHAdded: PropTypes.func,
};

export default ChoosePaymentMethod;
